import { Transaction } from "@near-wallet-selector/core";

import meh from "@/assets/icons/meh.svg";
import nearIcon from "@/assets/icons/tokens/near.svg";
import wrapNearIcon from "@/assets/icons/tokens/wNEAR.svg";
import { NearRPCProviderService, TokenMetadata } from "@/services/rpc/near";

import { convertGas } from "./helpers";

const DECIMALS_DEFAULT_VALUE = 18;
const NEAR_TOKEN_ID = "near";
const WNEAR_TOKEN_ID = "wrap.near";
const STORAGE_TO_REGISTER_FT = "0.1";
const STORAGE_TO_REGISTER_WNEAR = "0.00125";
export const ONE_YOCTO = "1";

export const NEAR_TOKEN: TokenMetadata = {
  spec: "ft-1.0.0",
  name: "Near token",
  symbol: "NEAR",
  icon: nearIcon,
  reference: null,
  reference_hash: null,
  decimals: 24,
};

const defaultMetadata: TokenMetadata = {
  spec: "ft-1.0.0",
  name: "Token",
  symbol: "TKN",
  icon: meh,
  reference: null,
  reference_hash: null,
  decimals: DECIMALS_DEFAULT_VALUE,
};

export class FungibleTokenContract {
  readonly contractId: string;
  private provider: NearRPCProviderService;

  constructor(contractId: string) {
    this.contractId = contractId;
    this.provider = new NearRPCProviderService();
  }

  metadata: TokenMetadata = defaultMetadata;

  async getMetadata(): Promise<TokenMetadata | null> {
    try {
      if (this.contractId === NEAR_TOKEN_ID) {
        this.metadata = NEAR_TOKEN;
        return NEAR_TOKEN;
      }

      const metadata = await this.provider.viewFunction("ft_metadata", this.contractId, {});
      if (!metadata) return defaultMetadata;
      if (this.contractId === WNEAR_TOKEN_ID) metadata.icon = wrapNearIcon;

      if (!metadata.icon) metadata.icon = defaultMetadata.icon;
      if (!metadata.decimals) metadata.decimals = defaultMetadata.decimals;

      this.metadata = metadata;
      return metadata;
    } catch (e) {
      console.error(`Error while loading ${this.contractId}`);
    }
    return null;
  }

  private async getStorageBalanceBounds() {
    return this.provider.viewFunction("storage_balance_bounds", this.contractId, {});
  }

  private async getStorageBalance({ accountId }: { accountId: string }) {
    return this.provider.viewFunction("storage_balance_of", this.contractId, { account_id: accountId });
  }

  async getBalance({ accountId }: { accountId: string }) {
    return this.provider.viewFunction("ft_balance_of", this.contractId, { account_id: accountId });
  }

  async checkStorageBalance({ accountId }: { accountId: string }) {
    try {
      if (this.contractId === NEAR_TOKEN_ID || this.contractId === "usn") return undefined;
      const storageBalance = await this.getStorageBalance({ accountId });

      if (!storageBalance) {
        const defaultStorageAmount =
          this.contractId === WNEAR_TOKEN_ID ? STORAGE_TO_REGISTER_WNEAR : STORAGE_TO_REGISTER_FT;

        const storageAmount = (await this.getStorageBalanceBounds())?.max || defaultStorageAmount;
        return this.createStorageDepositTransaction({ accountId, amount: storageAmount });
      }
      return undefined;
    } catch (e) {
      return undefined;
    }
  }

  private createStorageDepositTransaction({ accountId, amount }: { accountId: string; amount: string }): Transaction {
    return {
      signerId: accountId,
      receiverId: this.contractId,
      actions: [
        {
          type: "FunctionCall",
          params: {
            methodName: "storage_deposit",
            args: {
              account_id: accountId,
            },
            deposit: amount,
            gas: convertGas(),
          },
        },
      ],
    };
  }
}
